import React, { useState } from "react";
import { toast } from "sonner";
import Seo from "../components/site/Seo";
import PageHero from "../components/site/PageHero";
import Reveal from "../components/site/Reveal";
import { SectionLabel, PrimaryButton, ArrowLink } from "../components/site/Bits";
import { Field, TextAreaField, SelectField, FormSuccess } from "../components/site/Fields";
import ClosingCTA from "../components/home/ClosingCTA";
import { SEO, SOCIAL_IMPACT, FOCUS_AREAS, HOW_IT_WORKS, COMPANY } from "../lib/content";
import { IMAGES } from "../lib/images";
import { postCampaign, extractError } from "../lib/api";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[a-zA-Z]{2,}$/;

const emptyForm = {
  cause_title: "",
  focus_area: "",
  organisation: "",
  contact_name: "",
  email: "",
  phone: "",
  location: "",
  story: "",
};

export default function SocialImpactPage() {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (form.cause_title.trim().length < 3) next.cause_title = "Please give the cause a title.";
    if (!form.focus_area) next.focus_area = "Please choose a focus area.";
    if (form.contact_name.trim().length < 2) next.contact_name = "Please enter a contact name.";
    if (!EMAIL_RE.test(form.email.trim())) next.email = "Please enter a valid email address.";
    if (form.phone.trim().length < 6) next.phone = "Please enter a contact number.";
    if (form.story.trim().length < 10) next.story = "Please share a little about the cause.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await postCampaign(form);
      setSent(true);
      setForm(emptyForm);
      toast.success("Your cause has been submitted for verification.");
    } catch (error) {
      toast.error(extractError(error));
    } finally {
      setSending(false);
    }
  };

  const scrollToForm = () => {
    const el = document.getElementById("start-a-campaign");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <Seo title={SEO["social-impact"].title} description={SEO["social-impact"].description} path="/social-impact" />

      <PageHero
        eyebrow="Social Impact"
        titleLines={[SOCIAL_IMPACT.heading]}
        tagline={SOCIAL_IMPACT.tagline}
        intro={SOCIAL_IMPACT.body}
        image={IMAGES.socialImpact.hero}
      >
        <PrimaryButton variant="ivory" onClick={scrollToForm} data-testid="social-impact-hero-cta">
          {SOCIAL_IMPACT.campaignCta}
        </PrimaryButton>
      </PageHero>

      {/* focus areas */}
      <section className="lk-section" data-testid="focus-areas-section">
        <div className="lk-container-wide">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <Reveal>
                <SectionLabel>Our Focus Areas</SectionLabel>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="display-lg mt-7 max-w-[20ch]">WHERE SUPPORT MATTERS MOST.</h2>
              </Reveal>
            </div>
            <Reveal delay={0.14}>
              <p className="max-w-[34ch] text-[14px] leading-[1.6] body-muted">
                Seven areas where storytelling and collective participation can change an outcome.
              </p>
            </Reveal>
          </div>

          <div className="mt-[clamp(28px,3vw,48px)] grid gap-x-12 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
            {FOCUS_AREAS.map((area, i) => (
              <Reveal key={area.title} variant="image" delay={(i % 3) * 0.06} data-testid="focus-area-card">
                <div className="img-frame img-zoom" style={{ aspectRatio: i === 0 ? "4 / 3" : "16 / 11" }}>
                  <img src={IMAGES.socialImpact.focus[area.title]} alt={area.title} loading="lazy" />
                </div>
                <h3 className="mt-6 font-display text-[clamp(18px,1.5vw,22px)] font-extrabold tracking-[-0.02em]">
                  {area.title}
                </h3>
                <p className="mt-3 max-w-[34ch] text-[14px] leading-[1.62] body-muted">{area.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* crowdfunding */}
      <section className="cinematic relative isolate overflow-hidden" data-testid="crowdfunding-section">
        <div className="absolute inset-0 z-0" aria-hidden="true">
          <img
            src={IMAGES.socialImpact.crowdfunding}
            alt=""
            className="h-full w-full object-cover"
            style={{ opacity: 0.24 }}
            loading="lazy"
          />
        </div>
        <div
          className="grain absolute inset-0 z-10"
          aria-hidden="true"
          style={{ background: "linear-gradient(120deg, rgba(14,13,20,0.95) 25%, rgba(14,13,20,0.7) 100%)" }}
        />
        <div className="lk-container-wide relative z-20 py-[clamp(40px,4.2vw,70px)]">
          <SectionLabel onDark>Crowdfunding</SectionLabel>
          <h2 className="display-xl mt-8 max-w-[18ch]" style={{ color: "#FBFAF7" }}>
            {SOCIAL_IMPACT.crowdfundingHeading.map((line, i) => (
              <span key={line} className="block" style={i === 2 ? { color: "#E7D6B2" } : undefined}>
                {line}
              </span>
            ))}
          </h2>

          <div className="mt-10">
            <span className="eyebrow" style={{ color: "rgba(247,246,242,0.5)" }}>
              How It Works
            </span>
            <ol
              className="mt-8 grid gap-x-12 gap-y-0 sm:grid-cols-2 lg:grid-cols-3"
              style={{ listStyle: "none", margin: 0, padding: 0 }}
            >
              {HOW_IT_WORKS.map((step) => (
                <li
                  key={step.num}
                  data-testid={`how-it-works-step-${step.num}`}
                  className="py-7"
                  style={{ borderTop: "1px solid rgba(247,246,242,0.16)" }}
                >
                  <span
                    className="font-display text-[12px] font-bold"
                    style={{ letterSpacing: "0.16em", color: "rgba(216,183,122,0.9)" }}
                  >
                    {step.num}
                  </span>
                  <h3 className="mt-3 font-display text-[clamp(17px,1.4vw,21px)] font-extrabold" style={{ color: "#FBFAF7", letterSpacing: "0.02em" }}>
                    {step.title}
                  </h3>
                  <p className="mt-3 max-w-[32ch] text-[14px] leading-[1.62]" style={{ color: "rgba(247,246,242,0.7)" }}>
                    {step.text}
                  </p>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-7">
            <PrimaryButton variant="ivory" onClick={scrollToForm} data-testid="crowdfunding-start-campaign-cta">
              {SOCIAL_IMPACT.campaignCta}
            </PrimaryButton>
            <ArrowLink href={`mailto:${COMPANY.email}`} onDark data-testid="social-impact-email-link">
              {COMPANY.email}
            </ArrowLink>
          </div>
        </div>
      </section>

      {/* start a campaign */}
      <section className="lk-section" id="start-a-campaign" data-testid="start-campaign-section">
        <div className="lk-container-wide">
          <div className="grid gap-y-12 lg:grid-cols-12 lg:gap-x-16">
            <div className="lg:col-span-5">
              <Reveal>
                <SectionLabel>Start a Campaign</SectionLabel>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="display-lg mt-7 max-w-[16ch]">SUBMIT A CAUSE FOR VERIFICATION.</h2>
                <p className="mt-7 lead measure">
                  Share the cause and its requirement. Every submission is reviewed and verified before any campaign
                  goes live.
                </p>
              </Reveal>
            </div>

            <div className="lg:col-span-6 lg:col-start-7">
              {sent ? (
                <FormSuccess
                  testId="campaign-success"
                  title="CAUSE SUBMITTED."
                  message="Thank you. Your cause has been submitted for verification and our team will be in touch with the next steps."
                >
                  <PrimaryButton variant="outline" onClick={() => setSent(false)} data-testid="campaign-reset">
                    Submit Another Cause
                  </PrimaryButton>
                </FormSuccess>
              ) : (
                <form onSubmit={onSubmit} data-testid="campaign-form" noValidate>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <Field
                        label="Cause Title"
                        name="cause_title"
                        required
                        value={form.cause_title}
                        onChange={onChange}
                        error={errors.cause_title}
                        placeholder="What is this campaign for?"
                      />
                    </div>
                    <SelectField
                      label="Focus Area"
                      name="focus_area"
                      required
                      value={form.focus_area}
                      onChange={onChange}
                      options={FOCUS_AREAS.map((a) => a.title)}
                      error={errors.focus_area}
                      placeholder="Select a focus area"
                    />
                    <Field
                      label="Organisation"
                      name="organisation"
                      value={form.organisation}
                      onChange={onChange}
                      placeholder="If applicable"
                    />
                    <Field
                      label="Contact Name"
                      name="contact_name"
                      required
                      value={form.contact_name}
                      onChange={onChange}
                      error={errors.contact_name}
                      placeholder="Your name"
                    />
                    <Field
                      label="Email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={onChange}
                      error={errors.email}
                      placeholder="you@email.com"
                    />
                    <Field
                      label="Phone"
                      name="phone"
                      required
                      value={form.phone}
                      onChange={onChange}
                      error={errors.phone}
                      placeholder="+91"
                    />
                    <Field
                      label="Location"
                      name="location"
                      value={form.location}
                      onChange={onChange}
                      placeholder="City / State"
                    />
                    <div className="sm:col-span-2">
                      <TextAreaField
                        label="The Story"
                        name="story"
                        required
                        rows={5}
                        value={form.story}
                        onChange={onChange}
                        error={errors.story}
                        placeholder="Tell us about the cause, who it supports and what is needed."
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="campaign-submit-button"
                    className="btn btn-primary mt-9"
                    style={sending ? { opacity: 0.7, cursor: "wait" } : undefined}
                  >
                    {sending ? "Submitting…" : "Start a Campaign"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <ClosingCTA />
    </>
  );
}
